import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getTrips, deleteTrip } from '../services/firebaseService';
import { convertCurrency, convertAndFormatCurrency, formatCurrency, getAvailableCurrencies } from '../utils/currencyConverter';
import { Plus, MapPin, DollarSign, Users, LogOut, RefreshCw, Trash2, ChevronDown, Home, Calendar } from 'lucide-react';
import logo from '../assets/logo.png';

const Dashboard = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllTrips, setShowAllTrips] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('INR');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const savedCurrency = localStorage.getItem('preferredCurrency');
    if (savedCurrency) {
      setSelectedCurrency(savedCurrency);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('preferredCurrency', selectedCurrency);
  }, [selectedCurrency]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCurrencyDropdown && !event.target.closest('.currency-selector')) {
        setShowCurrencyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCurrencyDropdown]);

  const getDisplayName = (user) => {
    if (user.displayName) return user.displayName;
    if (user.email) {
      const name = user.email.split('@')[0];
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
    return 'User';
  };

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        if (currentUser) {
          console.log('🔍 Fetching trips for user:', currentUser.displayName || currentUser.email);
          console.log('📧 User UID:', currentUser.uid);
          
          const userTrips = await getTrips(currentUser.email);
          console.log('✅ Fetched trips:', userTrips);
          console.log('📊 Trips count:', userTrips.length);
          
          const processedTrips = userTrips.map(trip => ({
            ...trip,
            createdAt: trip.createdAt ? (trip.createdAt.toDate ? trip.createdAt.toDate() : new Date(trip.createdAt)) : new Date(),
            totalSpent: trip.totalSpent || 0
          }));
          
          console.log('📈 Processed trips:', processedTrips);
          setTrips(processedTrips);
        } else {
          console.log('❌ No current user found');
          setTrips([]);
        }
      } catch (error) {
        console.error('❌ Error fetching trips:', error);
        console.error('Error details:', error.code, error.message);
        setTrips([]);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchTrips();
    } else {
      console.log('⏳ Waiting for user authentication...');
      setTrips([]);
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && currentUser) {
        console.log('🔄 Page became visible, refreshing trips...');
        const fetchTrips = async () => {
          try {
            const userTrips = await getTrips(currentUser.email);
            const processedTrips = userTrips.map(trip => ({
              ...trip,
              createdAt: trip.createdAt ? (trip.createdAt.toDate ? trip.createdAt.toDate() : new Date(trip.createdAt)) : new Date(),
              totalSpent: trip.totalSpent || 0
            }));
            setTrips(processedTrips);
            console.log('✅ Trips refreshed on visibility change');
          } catch (error) {
            console.error('❌ Error refreshing trips on visibility change:', error);
          }
        };
        fetchTrips();
      }
    };

    const handleWindowFocus = () => {
      if (currentUser) {
        console.log('🔄 Window focused, refreshing trips...');
        const fetchTrips = async () => {
          try {
            const userTrips = await getTrips(currentUser.email);
            const processedTrips = userTrips.map(trip => ({
              ...trip,
              createdAt: trip.createdAt ? (trip.createdAt.toDate ? trip.createdAt.toDate() : new Date(trip.createdAt)) : new Date(),
              totalSpent: trip.totalSpent || 0
            }));
            setTrips(processedTrips);
            console.log('✅ Trips refreshed on window focus');
          } catch (error) {
            console.error('❌ Error refreshing trips on window focus:', error);
          }
        };
        fetchTrips();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleDeleteTrip = async (tripId) => {
    setIsDeleting(true);
    try {
      console.log('🗑️ Deleting trip:', tripId);
      await deleteTrip(tripId);
      console.log('✅ Trip deleted successfully');
      
      setTrips(prevTrips => prevTrips.filter(trip => trip.id !== tripId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('❌ Error deleting trip:', error);
      alert('Failed to delete trip. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmDeleteTrip = (trip) => {
    setDeleteConfirm(trip);
  };

  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  const handleRefresh = async () => {
    if (!currentUser) return;
    
    setRefreshing(true);
    try {
      console.log('🔄 Manually refreshing trips...');
      const userTrips = await getTrips(currentUser.email);
      const processedTrips = userTrips.map(trip => ({
        ...trip,
        createdAt: trip.createdAt ? (trip.createdAt.toDate ? trip.createdAt.toDate() : new Date(trip.createdAt)) : new Date(),
        totalSpent: trip.totalSpent || 0
      }));
      setTrips(processedTrips);
      console.log('✅ Trips refreshed successfully');
    } catch (error) {
      console.error('❌ Error refreshing trips:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const calculateTotalSpent = () => {
    return trips.reduce((sum, trip) => {
      const tripTotal = trip.totalSpent || 0;
      const tripCurrency = trip.currency || 'USD';
      const convertedAmount = convertCurrency(tripTotal, tripCurrency, selectedCurrency);
      return sum + convertedAmount;
    }, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-olive-50 via-green-50 to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-olive-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your adventures...</p>
        </div>
      </div>
    );
  }

  const displayedTrips = showAllTrips ? trips : trips.slice(0, 3);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Premium Background with Textures */}
      <div className="absolute inset-0 bg-gradient-to-br from-olive-900 via-olive-800 to-emerald-900">
        {/* Texture Overlay */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        {/* Gradient Overlay for Depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-transparent"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 left-20 w-32 h-32 bg-olive-600/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-20 w-40 h-40 bg-emerald-600/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-green-600/20 rounded-full blur-2xl animate-pulse delay-500"></div>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Premium Header */}
        <header className="bg-white/10 backdrop-blur-2xl shadow-2xl border-b border-white/20 relative z-[9999998]">
          <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16">
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-r from-olive-600 to-green-600 rounded-xl flex items-center justify-center shadow-2xl border border-white/20">
                    <img src={logo} alt="TravelSplit" className="w-6 h-6 sm:w-10 sm:h-10 object-contain" />
                  </div>
                  <span className="text-xl sm:text-3xl font-black tracking-wide" style={{ fontFamily: 'Playfair Display, Georgia, serif', letterSpacing: '0.05em' }}>
                    <span className="text-black">Travel</span>
                    <span style={{ color: '#166534' }}>Split</span>
                  </span>
                </div>
                <div className="hidden sm:block h-8 w-px bg-gradient-to-b from-olive-200 to-green-200"></div>
                <div className="text-xs sm:text-sm text-gray-800 font-medium hidden sm:block">
                  <span className="font-medium">👋</span> {getDisplayName(currentUser)}
                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className="relative currency-selector">
                  <button
                    onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                    className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 text-xs sm:text-sm bg-white/20 backdrop-blur border border-white/30 rounded-lg hover:bg-white/30 transition-all duration-300"
                  >
                    <span className="text-sm sm:text-lg">💰</span>
                    <span className="font-medium text-gray-800 hidden sm:inline">{selectedCurrency}</span>
                    <span className="font-medium text-gray-800 sm:hidden">{selectedCurrency.slice(0, 2)}</span>
                    <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 text-olive-200" />
                  </button>
                  
                  {showCurrencyDropdown && (
                    <div className="absolute top-full right-0 mt-2 w-48 bg-white/95 backdrop-blur-2xl border border-white/30 rounded-xl shadow-2xl"
                         style={{ zIndex: 9999999 }}>
                      <div className="py-2 max-h-64 overflow-y-auto">
                        {getAvailableCurrencies().map((currency) => (
                          <div
                            key={currency.code}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedCurrency(currency.code);
                              setShowCurrencyDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-olive-600/20 transition-colors cursor-pointer ${
                            selectedCurrency === currency.code ? 'bg-olive-600/30 text-olive-900 font-medium' : 'text-gray-800'
                          }`}
                          >
                            <span className="font-medium">{currency.code}</span>
                            <span className="text-gray-600 ml-2">({currency.symbol})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={refreshing || !currentUser}
                  className="flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm bg-white/20 backdrop-blur border border-white/30 rounded-lg hover:bg-white/30 transition-all duration-300 disabled:opacity-50"
                  title="Refresh trips"
                >
                  <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 text-olive-200 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
                <Link
                  to="/create-trip"
                  className="hidden sm:flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 text-white font-bold rounded-lg hover:from-gray-900 hover:via-gray-800 hover:to-gray-900 transition-all duration-300 shadow-xl hover:shadow-2xl"
                >
                  <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Create Trip</span>
                  <span className="sm:hidden">+</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center px-2 sm:px-4 py-2 text-xs sm:text-sm bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg hover:from-emerald-700 hover:to-teal-700 transition-all duration-300 shadow-xl hover:shadow-2xl"
                >
                  <LogOut className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Logout</span>
                  <span className="sm:hidden">⏻</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
          {/* Premium Stats Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="relative group h-24 sm:h-32">
              <div className="absolute -inset-1 bg-gradient-to-r from-green-600 to-teal-600 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-all"></div>
              <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/20 p-3 sm:p-6 overflow-hidden h-full">
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-2xl"></div>
                <div className="relative flex items-center h-full">
                  <div className="p-2 sm:p-3 bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg">
                    <MapPin className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="ml-2 sm:ml-4">
                    <p className="text-xs sm:text-sm text-gray-900 font-medium">Total Trips</p>
                    <p className="text-lg sm:text-2xl font-bold text-gray-900 drop-shadow-lg">{trips.length}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative group h-24 sm:h-32">
              <div className="absolute -inset-1 bg-gradient-to-r from-emerald-600 to-green-600 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-all"></div>
              <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/20 p-3 sm:p-6 overflow-hidden h-full">
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-2xl"></div>
                <div className="relative flex items-center h-full">
                  <div className="p-2 sm:p-3 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg">
                    <DollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="ml-2 sm:ml-4">
                    <p className="text-xs sm:text-sm text-gray-900 font-medium">Total Spent</p>
                    <p className="text-lg sm:text-2xl font-bold text-gray-900 drop-shadow-lg">
                      {formatCurrency ? formatCurrency(calculateTotalSpent(), selectedCurrency) : convertAndFormatCurrency(calculateTotalSpent(), 'USD', selectedCurrency)}
                    </p>
                    <p className="text-xs text-gray-700">in {selectedCurrency}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative group h-24 sm:h-32">
              <div className="absolute -inset-1 bg-gradient-to-r from-green-600 to-teal-600 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-all"></div>
              <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/20 p-3 sm:p-6 overflow-hidden h-full">
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-2xl"></div>
                <div className="relative flex items-center h-full">
                  <div className="p-2 sm:p-3 bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg">
                    <Users className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="ml-2 sm:ml-4">
                    <p className="text-xs sm:text-sm text-gray-900 font-medium">Total Members</p>
                    <p className="text-lg sm:text-2xl font-bold text-gray-900 drop-shadow-lg">
                      {trips.reduce((sum, trip) => sum + trip.members.length, 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Premium Action Buttons */}
          <div className="mb-6 sm:mb-8 flex flex-wrap gap-2 sm:gap-4">
            <Link
              to="/create-trip"
              className="relative inline-flex items-center px-3 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 text-white font-bold rounded-xl hover:from-gray-900 hover:via-gray-800 hover:to-gray-900 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98] text-xs sm:text-sm"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-xl opacity-0 hover:opacity-100 transition-opacity"></div>
              <div className="relative flex items-center">
                <Plus className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Plan New Adventure</span>
                <span className="sm:hidden">New Trip</span>
              </div>
            </Link>
            {!showAllTrips && trips.length > 3 && (
              <button
                onClick={() => setShowAllTrips(true)}
                className="relative inline-flex items-center px-3 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 text-white font-bold rounded-xl hover:from-gray-900 hover:via-gray-800 hover:to-gray-900 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98] text-xs sm:text-sm"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-xl opacity-0 hover:opacity-100 transition-opacity"></div>
                <div className="relative flex items-center">
                  <Calendar className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">View All Adventures ({trips.length - 3})</span>
                  <span className="sm:hidden">All ({trips.length - 3})</span>
                </div>
              </button>
            )}
          </div>

          {/* Premium Trips Section */}
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">
              <span className="bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 bg-clip-text text-transparent">
                {showAllTrips ? 'All Your Adventures' : 'Recent Adventures'}
              </span>
            </h2>
            
            {trips.length === 0 ? (
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-olive-600 to-emerald-600 rounded-2xl blur-lg opacity-30"></div>
                <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/20 p-12 text-center overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-2xl"></div>
                  <div className="relative">
                    <div className="text-6xl mb-4">🌍</div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">No adventures yet</h3>
                    <p className="text-gray-700 mb-6">Start planning your first adventure and split expenses with friends!</p>
                    <Link
                      to="/create-trip"
                      className="inline-flex items-center px-3 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 text-white font-bold rounded-xl hover:from-gray-900 hover:via-gray-800 hover:to-gray-900 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98] text-xs sm:text-sm"
                    >
                      <Plus className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                      Create Your First Adventure
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                {displayedTrips.map((trip) => (
                  <div key={trip.id} className="relative group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-olive-600 to-emerald-600 rounded-2xl blur-lg opacity-40 group-hover:opacity-60 transition-all"></div>
                    <div className="relative bg-white/15 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/30 p-3 sm:p-6 hover:shadow-3xl transition-all duration-300 overflow-hidden"
                     style={{
                       background: `repeating-linear-gradient(135deg, 
                        rgba(22, 101, 52, 0.08) 0px, 
                        transparent 25px, 
                        rgba(22, 101, 52, 0.06) 25px, 
                        transparent 50px, 
                        rgba(22, 101, 52, 0.08) 50px, 
                        transparent)`,
                       backgroundSize: '50px 50px',
                       backgroundPosition: 'center'
                     }}>
                      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-2xl"></div>
                      <div className="relative">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-r from-olive-600 to-green-600 rounded-xl flex items-center justify-center shadow-xl border border-white/30">
                              <img src={logo} alt="TravelSplit" className="w-6 h-6 sm:w-9 sm:h-9 object-contain" />
                            </div>
                            <h3 className="text-sm sm:text-lg font-bold text-gray-900 drop-shadow-lg">
                              {trip.name || trip.title || trip.tripName || 'Untitled Trip'}
                            </h3>
                          </div>
                          <div className="text-xs sm:text-sm text-gray-800 font-medium">
                            {trip.currency || 'USD'} • {trip.members ? trip.members.length : 0} {trip.members.length === 1 ? 'member' : 'members'}
                          </div>
                        </div>
                        
                        <div className="space-y-4">
                          <div className="flex items-center text-xs sm:text-sm text-gray-700">
                            <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-olive-600" />
                            {trip.createdAt ? new Date(trip.createdAt).toLocaleDateString() : 'No date'}
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm sm:text-xl font-bold text-gray-900 drop-shadow-lg">
                                {(trip.totalSpent || 0) ? convertAndFormatCurrency(trip.totalSpent, trip.currency || 'USD', selectedCurrency) : `${trip.currency || 'USD'} 0.00`}
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                Original: {trip.currency || 'USD'} {(trip.totalSpent || 0) ? trip.totalSpent.toFixed(2) : '0.00'}
                              </div>
                            </div>
                            <div className="text-xs sm:text-sm text-gray-700 font-medium">
                              Total spent
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-white/30">
                          <Link
                            to={`/trip/${trip.id}`}
                            className="flex items-center text-olive-700 hover:text-olive-900 font-semibold text-xs sm:text-sm transition-all duration-200 hover:scale-105"
                          >
                            <span className="hidden sm:inline">View Details</span>
                            <span className="sm:hidden">View</span>
                            <Home className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                          </Link>
                          
                          <button
                            onClick={() => confirmDeleteTrip(trip)}
                            className="flex items-center text-red-600 hover:text-red-800 font-semibold text-xs sm:text-sm transition-all duration-200 hover:scale-105"
                          >
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                            <span className="hidden sm:inline">Delete</span>
                            <span className="sm:hidden">×</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Premium Delete Confirmation Modal */}
          {deleteConfirm && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-red-600 to-pink-600 rounded-2xl blur-lg opacity-50"></div>
                <div className="relative bg-white/90 backdrop-blur-2xl rounded-2xl p-4 sm:p-6 max-w-md w-full mx-2 sm:mx-4 border border-white/30 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-2xl"></div>
                  <div className="relative">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Delete Adventure</h3>
                    <p className="text-gray-700 mb-6 text-sm sm:text-base">
                      Are you sure you want to delete "<strong>{deleteConfirm.name}</strong>"? 
                      This action cannot be undone and all associated expenses will be permanently deleted.
                    </p>
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={cancelDelete}
                        disabled={isDeleting}
                        className="px-3 sm:px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 transition-colors duration-200 text-sm sm:text-base"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleDeleteTrip(deleteConfirm.id)}
                        disabled={isDeleting}
                        className="px-3 sm:px-4 py-2 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-lg hover:from-red-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg text-sm sm:text-base"
                      >
                        {isDeleting ? (
                          <span className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Deleting...
                          </span>
                        ) : (
                          'Delete Adventure'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
